import './Checkbox.scss';
import './Toggle.scss';

import PropTypes from 'prop-types';
import React, { cloneElement, useCallback, useMemo, useRef } from 'react';

import { ReactComponent as SvgCheck } from '@/common/assets/icons/check.svg';

const Checkbox = ({
  checked,
  className,
  context,
  disabled,
  errors,
  icon,
  id,
  name,
  onBlur,
  onChange,
  readOnly,
  required,
  title,
  type,
  value,
}) => {
  const inputRef = useRef();

  const classNames = [
    type,
    disabled ? `${type}--disabled` : null,
    checked ? `${type}--checked` : null,
    errors ? `${type}--error` : null,
    readOnly ? `${type}--readonly` : null,
    required ? `${type}--required` : null,
    className,
  ]
    .filter((val) => val)
    .join(' ');

  const stopPropagation = useCallback((e) => e.stopPropagation(), []);

  const Icon = useMemo(() => {
    if (icon) {
      return cloneElement(icon, { className: 'checkbox__icon' });
    }
    return <SvgCheck className="checkbox__icon" />;
  }, [icon]);

  const handleChange = useCallback(() => {
    if (!disabled && !readOnly) {
      onChange();
    }
  }, [disabled, onChange, readOnly]);

  return (
    <label
      aria-checked={checked}
      aria-disabled={disabled}
      aria-readonly={readOnly}
      aria-required={required}
      className={classNames}
      data-qa-id={`${context || name || 'checkbox'}-field-${
        disabled ? 'disabled' : 'active'
      }`}
      htmlFor={`${id}-input`}
      id={id}
      title={title}
    >
      {type === 'checkbox' && checked ? <>{Icon}</> : null}
      <input
        ref={inputRef}
        className={`${type}__input`}
        checked={checked}
        data-qa-id="input"
        disabled={disabled}
        id={`${id}-input`}
        name={name}
        onClick={stopPropagation}
        onKeyUp={stopPropagation}
        onChange={handleChange}
        onBlur={onBlur}
        readOnly={readOnly}
        required={required}
        tabIndex={-1}
        type="checkbox"
        value={value}
      />
    </label>
  );
};

Checkbox.defaultProps = {
  checked: false,
  className: '',
  context: null,
  disabled: false,
  errors: false,
  id: null,
  icon: null,
  name: '',
  onBlur: () => {},
  onChange: () => {},
  readOnly: false,
  required: false,
  title: null,
  type: 'checkbox',
  value: '',
};

Checkbox.propTypes = {
  checked: PropTypes.bool,
  className: PropTypes.string,
  context: PropTypes.string,
  disabled: PropTypes.bool,
  errors: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.element,
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  title: PropTypes.string,
  type: PropTypes.oneOf(['checkbox', 'toggle']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default Checkbox;
